import React, { ChangeEvent, useEffect, useMemo, useRef } from "react";
import { Pdf, UploadIcon } from "../../../assets/svg";
import toast from "react-hot-toast";
import { connect, useDispatch } from "react-redux";
import { getPreSignedUrl } from "../../../helper";
import { IoEyeOutline } from "react-icons/io5";
import { GoTrash } from "react-icons/go";
import DamageBox from "./DamageBox";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Damage } from "../../../types/damage";
import { RootState } from "../../../store/root-reducer";
import moment from "moment";
import { useTranslation } from "react-i18next";

import FieldBlock from "../../CreateClaim/FieldBlock";
import {
  handleClaimItemFileUpload,
  removeClaimItemDamage,
  removeClaimItemImage,
} from "../../../store/ace-claim/actions";
const fileAllowedExtensions = ["png", "jpg", "jpeg", "pdf"];
const accept = "image/jpeg, image/png, application/pdf";
const fileSizeLimit = 10 * 1024 * 1024;

function ClaimItemBox({ idx, item, claimId, plan, claimOptions }) {
  const documents_and_photos = item?.documents_and_photos || [];
  const claimItemId = item?.id;
  const { control, getValues, clearErrors } = useFormContext();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: `claimItemDamage.[${claimItemId}]`,
  });

  const disptach = useDispatch();
  const { t } = useTranslation();

  const {
    damageTypesOptions = [],
    occuredReasonOptions = [],
    actionTakenOptions = [],
  } = useMemo(() => {
    const { howDidOccur, actionsTaken, damageTypes } = claimOptions;
    const occuredReasonOptions =
      howDidOccur?.map((v) => ({
        label: v.name,
        value: v.id,
      })) || [];
    const actionTakenOptions =
      actionsTaken?.map((v) => ({
        label: v.name,
        value: v.id,
      })) || [];

    const damageTypesOptions =
      damageTypes?.map((v) => ({
        label: v.name,
        value: v.id,
      })) || [];
    return {
      occuredReasonOptions,
      actionTakenOptions,
      damageTypesOptions,
    };
  }, [claimOptions]);

  useEffect(() => {
    if (documents_and_photos.length > 0) {
      clearErrors(`claimItemDamage.[${claimItemId}_files]`);
    }
    return () => {};
  }, [claimItemId, clearErrors, documents_and_photos.length]);

  useEffect(() => {
    let index = 0;
    const updatedFormValues =
      getValues(`claimItemDamage.[${claimItemId}]`) || [];
    do {
      const damageData: Damage = item?.damages?.[index] || {};
      const safeIndex = index;
      const existFieldsIndex = updatedFormValues.findIndex(
        (field) => field?.["index"] === safeIndex
      );
      if (existFieldsIndex + 1) {
        update(existFieldsIndex, {
          ...(updatedFormValues?.[existFieldsIndex] || {}),
          damageId: damageData?.id,
          isSavedInDB: Boolean(damageData?.id),
        });
      } else {
        append({
          ...(damageData?.id ? { damageId: damageData?.id } : {}),
          index,
          isSavedInDB: Boolean(damageData?.id),
          damage_type: damageData?.type?.id || "",
          specific_damage: damageData?.specificDamage?.id || "",
          damage_area: damageData?.specificLocation?.id || "",
          how_did_it_happen: damageData?.occuredReason?.id || "",
          did_you_take_any_steps_to_remedy: damageData?.actionTaken?.id || "",
          when_did_you_notice_the_damage: damageData?.damageDate
            ? moment(damageData?.damageDate).format("YYYY-MM-DD")
            : null,
          note: damageData?.notes || "",
        });
      }
      index++;
    } while (index < item?.damages?.length);

    return () => {};
  }, [append, claimItemId, getValues, item?.damages, item?.id, update]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { files: inputFiles },
    } = event;
    event.persist();
    if (!inputFiles) {
      return;
    }

    const fileArray = Array.from(inputFiles);

    for (const idx in fileArray) {
      const file = fileArray[idx];
      if (fileSizeLimit && file.size > fileSizeLimit) {
        toast.error(
          `File is too big. Should be less then ${
            fileSizeLimit / (1024 * 1024)
          } MB`,
          {
            position: "bottom-center",
          }
        );
        event.target.value = "";
        return;
      }
      const fileName = file.name;
      const fileExtension = fileName.substring(
        fileName.lastIndexOf(".") + 1,
        fileName?.length
      );
      if (!fileAllowedExtensions.includes(fileExtension.toLowerCase())) {
        toast.error(`File ${file.name} is not supported`, {
          position: "bottom-center",
        });
        event.target.value = "";
        return;
      }
    }
    if (fileArray && fileArray.length > 0) {
      disptach(
        handleClaimItemFileUpload({
          files: fileArray,
          claimItemId: item.id,
        })
      );
    }
    event.target.value = "";
    clearErrors(`claimItemDamage.[${claimItemId}_files]`);
  };

  const handleDeleteClick = (idx, item) => {
    if (item.isSavedInDB) {
      disptach(removeClaimItemDamage(item?.damageId));
    }
    remove(idx);
  };

  return (
    <div>
      <div className="bg-[#EBEEEF] min-h-8 rounded flex items-center px-2 gap-2 overflow-hidden">
        <span className="size-5 flex justify-center items-center  bg-white rounded-full p-3">
          {idx + 1}
        </span>
        <div className="text-black font-semibold  block">
          {item?.type?.name}
        </div>
      </div>
      <div className="bg-white grid gap-1.5 divide-y-[1px] divide-[#F0F0F0] px-2 py-2">
        <FieldBlock label="Purchase Date" value={plan.purchaseDate} date />
        <FieldBlock label="Store Code" value={plan?.store} />
        <FieldBlock label="Manufacturer" value={item.manufacturer?.name} />
        <FieldBlock label="Color" value={item.color?.name} />
        <FieldBlock label="Material" value={item.material?.name} />
      </div>
      <Controller
        name={`claimItemDamage.[${claimItemId}_files]`}
        shouldUnregister
        render={({ field, fieldState: { error } }) => (
          <div className="flex flex-col  items-center my-3 space-y-2">
            <p>{t("Upload required documents and photo(s):")} *</p>
            <div className="flex flex-col items-center gap-8 w-full">
              <div className="bg-[#2D6CCA14] w-full border-[#B5C2D1] border-2 border-dashed flex-col rounded flex justify-center items-center py-5 px-[18px] relative max-w-72">
                <UploadIcon size={48} />
                <span className="text-[#2D6CCA] text-sm text-center ">
                  {t("Upload you file..")}
                </span>

                <input
                  type="file"
                  title=" "
                  multiple
                  {...field}
                  onChange={(e) => {
                    handleFileChange(e);
                    field.onChange(e);
                  }}
                  className="absolute inset-0 text-transparent opacity-0 cursor-pointer"
                  accept={accept}
                />
              </div>
            </div>
            <p className="text-red-600 text-left  font-normal text-sm m-0 p-0  bottom-0 left-0 right-0">
              {error?.message}
            </p>
          </div>
        )}
        rules={{
          validate: () => {
            return documents_and_photos?.length > 0
              ? true
              : "We need at least one photo from you";
          },
        }}
      />
      <div className="space-y-2">
        {documents_and_photos.length > 0 && (
          <>
            <div className="bg-[#F7F7F7] my-2 text-black font-semibold  min-h-8 rounded flex items-center px-2 gap-2 overflow-hidden">
              {t("Image Details")}
            </div>
            {documents_and_photos.map(({ id = "", fileName = "" }) => (
              <div
                className="flex flex-row  items-center justify-between"
                key={`${claimId}-${fileName}`}
              >
                <div className="flex  flex-row gap-2 items-center">
                  {fileName?.includes("pdf") ? (
                    <Pdf />
                  ) : (
                    <img
                      src={getPreSignedUrl({
                        claimId,
                        fileName,
                        prefix: "/ACE",
                      })}
                      className=" h-12  w-12 rounded-full aspect-square object-cover  shrink-0  overflow-hidden"
                      alt=""
                    />
                  )}
                  <p>{fileName}</p>
                </div>
                <div className="flex shrink-0 flex-row gap-2 items-center me-3">
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        getPreSignedUrl({ claimId, fileName, prefix: "/ACE" })
                      )
                    }
                  >
                    <IoEyeOutline color="#2D6CCA" size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      disptach(
                        removeClaimItemImage({
                          claimItemId: item.id,
                          docId: id,
                        })
                      )
                    }
                  >
                    <GoTrash color="#DB3C32" size={18} />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
        <div className="space-y-3">
          {fields?.map((damage, idx) => (
            <DamageBox
              key={damage?.id}
              idx={idx}
              item={damage}
              claimItemId={claimItemId}
              onDelete={handleDeleteClick}
              occuredReasonOptions={occuredReasonOptions}
              actionTakenOptions={actionTakenOptions}
              damageTypesOptions={damageTypesOptions}
            />
          ))}
          <button
            type="button"
            className="border-[#2D6CCA] p-2 text-[#2D6CCA] hover:bg-[#2D6CCA] hover:text-white text-sm font-medium  border rounded-md"
            onClick={() => {
              append({
                index: item.damages.length,
                damage_type: "",
                damage_area: "",
                how_did_it_happen: "",
                when_did_you_notice_the_damage: null,
                did_you_take_any_steps_to_remedy: "",
                note: "",
                isSavedInDB: false,
              });
            }}
          >
            {t("+ Add Damage")}
          </button>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  claimId: state?.aceClaim.claimId || "",
  plan: state?.aceClaim.plan || "",
  claimOptions: state?.user?.claimOptions || {},
});

export default connect(mapStateToProps)(ClaimItemBox);
